/**
 * Controller Card
 *
 * Displays a Hydrawise controller with its zones and controls.
 */

import React from 'react';
import { HydrawiseController, HydrawiseZone } from '../hydrawise-store';
import { ZoneCard } from './ZoneCard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Wifi, WifiOff } from 'lucide-react';

interface ControllerCardProps {
  controller: HydrawiseController;
  zones: HydrawiseZone[];
}

export const ControllerCard: React.FC<ControllerCardProps> = ({ controller, zones }) => {
  const isOnline = controller.status === 'online';
  const runningCount = zones.filter((z) => z.status === 'running').length;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            {isOnline ? (
              <Wifi className="h-4 w-4 text-green-500" />
            ) : (
              <WifiOff className="h-4 w-4 text-gray-400" />
            )}
            {controller.name}
          </CardTitle>
          <div className="flex items-center gap-2">
            {runningCount > 0 && (
              <Badge className="bg-blue-500 text-white">
                {runningCount} running
              </Badge>
            )}
            <Badge variant={isOnline ? 'default' : 'secondary'}>
              {isOnline ? 'Online' : 'Offline'}
            </Badge>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Last contact: {new Date(controller.lastContact).toLocaleString()}
          {controller.serialNumber && ` · S/N: ${controller.serialNumber}`}
        </p>
      </CardHeader>
      <CardContent>
        {zones.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">No zones found.</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {zones.map((zone) => (
              <ZoneCard key={zone.relayId} zone={zone} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
